import { useState } from 'react'
import { Typography, Input, Button, Spinner } from '@material-tailwind/react'
import { Link, Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { logger } from '../utils/helpers/logger'
import { resetPasswordEmail } from '../utils/helpers/supabase.helpers'
import { useSessionContext } from '../contexts/SessionContext'
import { useGeneralToast } from '../hooks/toast'
import { usePageTitle } from '../hooks/toolkit'
import Container from '../components/Container'
import { useDarkMode } from '../contexts/DarkModeContext'

const ResetPassword = () => {
  const { t } = useTranslation()

  usePageTitle(t('resetPassword.pageTitle'))

  const { user } = useSessionContext()
  const location = useLocation()
  const { darkMode } = useDarkMode()
  const { success: toastSuccess, error: toastError, warning: toastWarn } = useGeneralToast()

  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)

  const forgotPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.stopPropagation()
    e.preventDefault()

    if (!email) {
      toastWarn(t('resetPassword.missingEmail'))
      return
    }

    setLoading(true)

    const { error } = await resetPasswordEmail({ email })

    if (error) {
      toastError(t('resetPassword.error'))
      logger.error(error, 'ResetPassword')
    } else {
      toastSuccess(t('resetPassword.success'))
    }

    setLoading(false)
  }

  if (user) {
    // send a logged user to its dashboard
    return <Navigate to="/dashboard" state={{ from: location }} replace />
  }

  return (
    <Container direction="col">
      <Typography className="mt-4 mb-12" variant="h1" data-testid="reset-password-title">
        {t('resetPassword.title')}
      </Typography>

      <form className="w-full md:w-1/2 flex flex-col gap-8" onSubmit={forgotPassword}>
        <Input
          {...(darkMode && { color: 'white' })}
          value={email || ''}
          onChange={(e) => setEmail(e.currentTarget.value)}
          type="email"
          label={t('auth.emailAddress')}
          required
          data-testid="reset-password-email-input"
        />
        <Button type="submit" disabled={loading} data-testid="reset-password-submit-btn">
          {loading ? <Spinner className="h-4 w-4 mx-auto" /> : t('resetPassword.submit')}
        </Button>
      </form>

      <hr className="w-full md:w-1/2 my-12 opacity-50" />

      <div className="flex w-full justify-center items-center gap-2">
        <Typography>{t('createAccount.alreadyHaveAccount')}</Typography>
        <Link to="/signin">
          <Typography className="underline font-bold">{t('auth.signIn')}</Typography>
        </Link>
      </div>
    </Container>
  )
}

export default ResetPassword
